/* eslint-disable indent */
import React, { useState } from "react";
import { connect } from "react-redux";
import { array, number, string, func, object, oneOfType } from "prop-types";
import DOMPurify from "dompurify";
import { Button, Modal } from "react-bootstrap";
import Select from "react-select";
import StyledEmailPreview from "components/StyledEmailPreview";

const EmailCandidatesComponent = (props) => {
  const {
    savedEmailTemplates,
    callLocation,
    changeAction,
    candidateIds,
    candidateName,
    testEventId,
    onEmailCandidates,
    onInviteCandidate,
    handleModalCancel,
  } = props;

  const [emailTemplateChosen, setEmailTemplateChosen] = useState(null);
  const [emailTemplateValid, setEmailTemplateValid] = useState(true);

  const emailDivStyle =
    callLocation === "search"
      ? null
      : {
          height: "calc(100vh - 80px)",
          minHeight: "330px",
          overflowY: "auto",
          marginTop: "0",
          paddingTop: "20px",
          paddingRight: "15px",
        };

  /* --------- Event Handlers ---------- */

  const handleChangeAction = (isActionDone) => {
    if (callLocation === "search") {
      handleModalCancel();
    } else {
      changeAction(false, isActionDone);
    }
  };

  const handleChooseTemplate = (event) => {
    const emailTemplate = savedEmailTemplates.get(event.value.toString());
    setEmailTemplateChosen(emailTemplate);
  };

  const handleSendEmail = () => {
    if (emailTemplateChosen === false && callLocation !== "testEventInfo") {
      setEmailTemplateValid(false);
    } else if (callLocation === "testEventInfo") {
      onInviteCandidate(testEventId, candidateName);
      handleChangeAction(true);
    } else {
      const dataToSend = {
        testTakerIds: candidateIds,
        emailTemplateId: emailTemplateChosen.get("emailTemplateId"),
      };
      onEmailCandidates(dataToSend);
      handleChangeAction(true);
    }
  };

  const savedEmailTemplateIds = savedEmailTemplates.keySeq();
  const emailTemplateDropdownArray = [];

  function createTemplateDropdownData(emailTemplateIds) {
    const sortedIds = emailTemplateIds.sort((tempId1, tempId2) =>
      savedEmailTemplates.get(tempId1).get("emailName").toLowerCase() >
      savedEmailTemplates.get(tempId2).get("emailName").toLowerCase()
        ? 1
        : -1
    );
    const emailTemplateArray = sortedIds.valueSeq().toArray();
    emailTemplateArray.forEach((templateId) => {
      const emailTemplate = savedEmailTemplates.get(templateId);
      emailTemplateDropdownArray.push({
        value: templateId,
        label: emailTemplate.get("emailName"),
      });
    });
    return [
      {
        label: "Email Templates",
        options: emailTemplateDropdownArray,
      },
    ];
  }

  const dropdownOptions = createTemplateDropdownData(savedEmailTemplateIds);

  const testEventInfoEmailBody = {
    __html:
      "<p>Dear {{ firstName }} {{ lastName }},</p><p>A test event has been scheduled for you by {{ companyName }}. You will need to click on the link to the Testing Center below, or enter the URL in your Web browser.</p><p>Please use the following information to access your test.</p><p>	<strong>Test Event ID: {{ testEventId }}</strong><br><strong>Testing Center: {{ testCenterLink }}</a><p><strong>Note:</strong> Please use Chrome, FireFox, Safari, Internet Explorer <strong>8 or newer</strong> to complete this test event. <strong>Please do not use your mobile phone.</strong></p><p><strong>For Help</strong>, please click the orange 'Get Help' button located at the bottom of the Testing Center screen.</p><p><em><a href='http://www.ondemandassessment.com/terms.htm#accommodations'>Disability/Special Accommodations</a>: In the event that you have a disability or require special accommodations, please notify the employer. You may also contact Candidate Support at 877.909.8378 or email us at <a href='mailto:support@criteriacorp.com'>support@criteriacorp.com</a>.</em></p><p>Test Administrator: {{ user_first_name }} {{ user_last_name }} (Click to send email)</p><hr><p>Please do not reply to this email. {{ user_first_name }} {{ user_last_name }} will not receive the reply.</p><hr>",
  };

  const emailBody = emailTemplateChosen
    ? { __html: DOMPurify.sanitize(emailTemplateChosen.get("emailBody")) }
    : null;

  const emailSubject = emailTemplateChosen
    ? emailTemplateChosen.get("emailSubject")
    : "{{ email_template_subject }}";

  const senderAlias = emailTemplateChosen
    ? `${
        emailTemplateChosen.get("senderAlias") || ""
      } < do-not-reply@criteriacorp.com >`
    : "< do-not-reply@criteriacorp.com >";

  return (
    <div style={emailDivStyle}>
      <Modal.Header closeButton={callLocation === "search"}>
        <h4>
          {callLocation === "testEventInfo"
            ? " Email Test Event Info to Candidate"
            : ` Email Candidate${candidateIds.length > 1 ? "s" : ""}`}
        </h4>
      </Modal.Header>
      <Modal.Body>
        {callLocation === "testEventInfo" ? null : (
          <p>
            <strong>Select an email template:</strong>
          </p>
        )}
        {callLocation === "testEventInfo" ? null : (
          <Select
            id="email_templates_dropdown"
            onChange={handleChooseTemplate}
            options={dropdownOptions}
            placeholder="Select an Email Template"
          />
        )}
        {emailTemplateValid ? null : (
          <p className="manual-override-invalid-feedback">
            Please select an email template
          </p>
        )}
        <br />
        <br />
        <p>
          <strong>Email preview:</strong>
        </p>
        <StyledEmailPreview callLocation={callLocation}>
          <p>
            From:{" "}
            {callLocation === "testEventInfo"
              ? "DO-NOT-REPLY <DO-NOT-REPLY@criteriacorp.com>"
              : senderAlias}
          </p>
          <p>
            Subject:{" "}
            {callLocation === "testEventInfo"
              ? "Test Event Notification"
              : emailSubject}
          </p>
          <hr />
          <div // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={
              callLocation === "testEventInfo"
                ? testEventInfoEmailBody
                : emailBody
            }
          />
        </StyledEmailPreview>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={() => handleChangeAction(false)}
          id="cancelEmailCandidate"
          variant="outline-primary"
          className="btn-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSendEmail}
          id="sendEmailCandidate"
        >
          Send Email
        </Button>
      </Modal.Footer>
    </div>
  );
};

EmailCandidatesComponent.propTypes = {
  savedEmailTemplates: object,
  callLocation: string,
  changeAction: func,
  candidateIds: array,
  candidateName: string,
  testEventId: oneOfType([number, string]),
  onEmailCandidates: func,
  onInviteCandidate: func,
  handleModalCancel: func,
};

const mapStateToProps = (state) => ({
  userData: state.get("App").get("userData"),
});

export default connect(mapStateToProps)(EmailCandidatesComponent);
